import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';

const ManageSettings = ({ isOpen, onClose }) => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Check if current user can manage settings
  const canManageSettings = hasPermission(currentUser, 'manage-settings');

  useEffect(() => {
    if (isOpen && canManageSettings) {
      loadSettings();
    }
  }, [isOpen, canManageSettings]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings`);
      
      if (!response.ok) {
        throw new Error(t('settings.failedToFetch'));
      }

      const data = await response.json();
      console.log('Loaded settings:', data.settings); // Debug logging
      setSettings(data.settings || {});
    } catch (err) {
      console.error('Error loading settings:', err);
      alert(t('settings.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [`${category}_${key}`]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      console.log('Saving settings:', settings); // Debug logging
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updated_by: currentUser.id,
          settings: settings
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.failedToSave'));
      }

      setHasChanges(false);
      alert(t('settings.savedSuccessfully'));
    } catch (err) {
      console.error('Error saving settings:', err);
      alert(t('settings.failedToSaveWithMessage', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm(t('settings.unsavedChanges'))) {
        setHasChanges(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  if (!canManageSettings) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: '#1f2937',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          width: '90%',
          maxWidth: '500px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <h2 style={{ margin: '0 0 16px 0', color: '#f9fafb' }}>{t('settings.accessDenied')}</h2>
          <p style={{ margin: '0 0 24px 0', color: '#d1d5db' }}>{t('settings.noPermission')}</p>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
          color: '#f9fafb',
          padding: '24px',
          borderBottom: '1px solid #374151',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#3b82f6',
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {t('settings.manageTitle')}
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af' }}>
                Configure application settings
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#9ca3af',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#374151';
              e.target.style.color = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#9ca3af';
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px',
              color: '#9ca3af'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid #374151',
                borderTop: '3px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '12px'
              }} />
              {t('settings.loading')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* Social Media Section */}
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #4b5563'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{
                      backgroundColor: '#059669',
                      borderRadius: '6px',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '16px' }}>🌐</span>
                    </div>
                    <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '18px', fontWeight: '600' }}>
                      {t('settings.socialMediaCTAs')}
                    </h3>
                  </div>
                  <p style={{ margin: 0, color: '#d1d5db', fontSize: '14px', lineHeight: '1.5' }}>
                    {t('settings.socialMediaDescription')}
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Telegram */}
                  <div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      color: '#f9fafb',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={settings.social_media_show_telegram_cta || false}
                        onChange={(e) => handleSettingChange('social_media', 'show_telegram_cta', e.target.checked)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      {t('settings.showTelegramCTA')}
                    </label>
                    <input
                      type="text"
                      value={settings.social_media_telegram_url || ''}
                      onChange={(e) => handleSettingChange('social_media', 'telegram_url', e.target.value)}
                      placeholder={t('settings.telegramPlaceholder')}
                      disabled={!settings.social_media_show_telegram_cta}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: settings.social_media_show_telegram_cta ? '#1f2937' : '#2d3748',
                        border: '1px solid #4b5563',
                        borderRadius: '8px',
                        color: settings.social_media_show_telegram_cta ? '#f9fafb' : '#9ca3af',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        fontFamily: 'monospace'
                      }}
                      onFocus={(e) => {
                        if (settings.social_media_show_telegram_cta) {
                          e.target.style.borderColor = '#3b82f6';
                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#4b5563';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Facebook */}
                  <div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      color: '#f9fafb',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={settings.social_media_show_facebook_cta || false}
                        onChange={(e) => handleSettingChange('social_media', 'show_facebook_cta', e.target.checked)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      {t('settings.showFacebookCTA')}
                    </label>
                    <input
                      type="text"
                      value={settings.social_media_facebook_url || ''}
                      onChange={(e) => handleSettingChange('social_media', 'facebook_url', e.target.value)}
                      placeholder={t('settings.facebookPlaceholder')}
                      disabled={!settings.social_media_show_facebook_cta}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: settings.social_media_show_facebook_cta ? '#1f2937' : '#2d3748',
                        border: '1px solid #4b5563',
                        borderRadius: '8px',
                        color: settings.social_media_show_facebook_cta ? '#f9fafb' : '#9ca3af',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        fontFamily: 'monospace'
                      }}
                      onFocus={(e) => {
                        if (settings.social_media_show_facebook_cta) {
                          e.target.style.borderColor = '#3b82f6';
                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#4b5563';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Tutorial Section */}
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #4b5563'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{
                      backgroundColor: '#7c3aed',
                      borderRadius: '6px',
                      padding: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '16px' }}>🎥</span>
                    </div>
                    <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '18px', fontWeight: '600' }}>
                      {t('settings.tutorialSettings')}
                    </h3>
                  </div>
                  <p style={{ margin: 0, color: '#d1d5db', fontSize: '14px', lineHeight: '1.5' }}>
                    {t('settings.tutorialDescription')}
                  </p>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    color: '#f9fafb',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '8px'
                  }}>
                    {t('settings.tutorialVideoURL')}
                  </label>
                  <input
                    type="url"
                    value={settings.tutorial_video_url || ''}
                    onChange={(e) => handleSettingChange('tutorial', 'video_url', e.target.value)}
                    placeholder={t('settings.tutorialURLPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#1f2937',
                      border: '1px solid #4b5563',
                      borderRadius: '8px',
                      color: '#f9fafb',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      fontFamily: 'monospace'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#7c3aed';
                      e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#4b5563';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <p style={{
                    margin: '8px 0 0 0',
                    color: '#9ca3af',
                    fontSize: '12px',
                    lineHeight: '1.4'
                  }}>
                    💡 {t('settings.tutorialURLHelp')}
                  </p>
                  {settings.tutorial_video_url && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#1f2937',
                      borderRadius: '6px',
                      border: '1px solid #4b5563'
                    }}>
                      <p style={{ margin: '0 0 8px 0', color: '#d1d5db', fontSize: '12px', fontWeight: '500' }}>
                        Preview URL:
                      </p>
                      <code style={{
                        color: '#60a5fa',
                        fontSize: '11px',
                        wordBreak: 'break-all',
                        backgroundColor: '#111827',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'block'
                      }}>
                        {settings.tutorial_video_url}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{
          borderTop: '1px solid #374151',
          padding: '20px 24px',
          backgroundColor: '#1f2937',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              padding: '10px 20px',
              backgroundColor: '#374151',
              color: '#d1d5db',
              border: '1px solid #4b5563',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: saving ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = '#4b5563';
                e.target.style.borderColor = '#6b7280';
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.backgroundColor = '#374151';
                e.target.style.borderColor = '#4b5563';
              }
            }}
          >
            {t('settings.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{
              padding: '10px 20px',
              backgroundColor: hasChanges && !saving ? '#3b82f6' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (hasChanges && !saving) {
                e.target.style.backgroundColor = '#2563eb';
              }
            }}
            onMouseLeave={(e) => {
              if (hasChanges && !saving) {
                e.target.style.backgroundColor = '#3b82f6';
              }
            }}
          >
            {saving && (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {saving ? t('settings.saving') : t('settings.saveChanges')}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ManageSettings;